import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Typography,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Chip,
    Snackbar,
    Alert,
    LinearProgress,
    MenuItem,
    Tooltip,
    Divider,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDispatch, useSelector } from 'react-redux';
import ModalBox from '../themes/overrides/ModalBox';
import TextFieldInput from '../themes/overrides/TextFieldInput';
import LoadingOverlay from './LoadingOverlay';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import {
    getEnvironments,
    addEnvironment,
    editEnvironment,
    removeEnvironment,
    waitForEnvironmentJob,
} from '../actions/environmentAction';

const SEED_OPTIONS = [
    { value: 'models', label: 'Content models only' },
    { value: 'models+entries', label: 'Content models and all entries' },
    { value: 'none', label: 'Start empty' },
];

/**
 * Create, rename and delete a workspace's environments.
 *
 * The default environment (Production) is what the public read API serves to
 * callers that name no environment, so it can be neither renamed away nor
 * deleted from here.
 */
const ManageEnvironmentsModal = ({ open, onClose, workspaceId, onSuccess }) => {
    const dispatch = useDispatch();
    const environments = useSelector((state) => state.environments.environments);

    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [copyFrom, setCopyFrom] = useState('production');
    const [seed, setSeed] = useState('models');
    const [editing, setEditing] = useState(null);
    const [editName, setEditName] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);
    const [progress, setProgress] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    // The poll loop outlives a render, so it reads "is this still open?" off a
    // ref rather than the captured prop.
    const openRef = useRef(open);
    useEffect(() => {
        openRef.current = open;
    }, [open]);

    useEffect(() => {
        if (open && workspaceId) {
            dispatch(getEnvironments(workspaceId, true));
        }
    }, [open, workspaceId, dispatch]);

    /**
     * Seeding and purging both run on the queue — they copy or delete every
     * entry one at a time — so the modal follows the job rather than pretending
     * the work finished when the request returned.
     */
    const followJob = async (jobId, label) => {
        setProgress({ label, state: 'queued', processed: 0, total: null });
        try {
            const finished = await waitForEnvironmentJob(workspaceId, jobId, {
                onProgress: (job) => setProgress({ label, ...job }),
                isCancelled: () => !openRef.current,
            });
            if (!finished || !openRef.current) return null;
            // Counts and warnings only settle when the job does.
            await dispatch(getEnvironments(workspaceId, true));
            return finished;
        } finally {
            setProgress(null);
        }
    };

    const handleCreate = async () => {
        if (!name.trim()) return;
        setLoading(true);
        try {
            const result = await dispatch(addEnvironment(workspaceId, { name, copyFrom, seed }));
            if (result?.status !== 'success') {
                notify(result?.message ?? 'Could not create the environment', 'error');
                return;
            }

            setName('');
            onSuccess?.();

            if (!result.jobId) {
                notify('Environment created');
                return;
            }

            setLoading(false);
            const finished = await followJob(result.jobId, `Seeding ${name.trim()}`);
            if (!finished) return;

            if (finished.state === 'failed') {
                // A failed seed withdraws the environment — an environment the
                // user believes is a copy, but holds an unknown slice of one,
                // is worse than no environment at all.
                notify(
                    `${finished.error ?? 'Seeding failed'} — the environment was removed, so you can create it again.`,
                    'error',
                );
                return;
            }
            notify(
                `Environment created — ${finished.result.contentTypesCreated} model(s) and ${finished.result.entriesCreated} entry(s) copied`,
            );
            if (finished.warnings?.length > 0) {
                notify(finished.warnings[0], 'warning');
            }
        } catch (error) {
            notify(error?.response?.data?.message ?? error?.message ?? 'Could not create the environment', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRename = async () => {
        setLoading(true);
        try {
            const result = await dispatch(editEnvironment(workspaceId, editing._id, { name: editName }));
            if (result?.status === 'success') {
                notify('Environment updated');
                setEditing(null);
                onSuccess?.();
            } else {
                notify(result?.message ?? 'Could not update the environment', 'error');
            }
        } catch (error) {
            notify(error?.response?.data?.message ?? 'Could not update the environment', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        const environmentName = pendingDelete?.name;
        setLoading(true);
        try {
            const result = await dispatch(removeEnvironment(workspaceId, pendingDelete._id));
            if (result?.status !== 'success') {
                notify(result?.message ?? 'Could not delete the environment', 'error');
                return;
            }

            setPendingDelete(null);
            onSuccess?.();

            if (!result.jobId) {
                notify('Environment deleted');
                return;
            }

            // It is already out of the list and out of the API; the job is
            // emptying it in the background.
            setLoading(false);
            const finished = await followJob(result.jobId, `Deleting ${environmentName}`);
            if (!finished) return;

            notify(
                finished.state === 'failed'
                    ? `${finished.error ?? 'Deletion failed'} — the environment stays hidden and its name is free to reuse, but some of its content was not removed.`
                    : `Environment deleted — ${finished.result.entriesRemoved} entry(s) and ${finished.result.contentTypesRemoved} model(s) removed`,
                finished.state === 'failed' ? 'error' : 'success',
            );
        } catch (error) {
            notify(error?.response?.data?.message ?? error?.message ?? 'Could not delete the environment', 'error');
        } finally {
            setPendingDelete(null);
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: '760px', maxWidth: '95%' }}>
                <Typography variant="h2" sx={{ mb: 1 }}>Environments</Typography>
                <Typography sx={{ color: '#ACACAC', mb: 3 }}>
                    An environment is an isolated copy of this workspace&apos;s content models and
                    entries. The API serves <strong>Production</strong> to any caller that does not
                    name one, so existing integrations are unaffected by the environments you add here.
                </Typography>

                <Box sx={{ p: 2.5, border: '1px solid #ECECEC', borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>New environment</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TextFieldInput
                            label="Name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            sx={{ flex: '1 1 200px' }}
                            placeholder="Staging"
                        />
                        <TextFieldInput
                            select
                            label="Copy from"
                            value={copyFrom}
                            onChange={(event) => setCopyFrom(event.target.value)}
                            sx={{ flex: '1 1 180px' }}
                        >
                            {environments.map((environment) => (
                                <MenuItem key={environment.slug} value={environment.slug}>
                                    {environment.name}
                                </MenuItem>
                            ))}
                        </TextFieldInput>
                        <TextFieldInput
                            select
                            label="Copy"
                            value={seed}
                            onChange={(event) => setSeed(event.target.value)}
                            sx={{ flex: '1 1 220px' }}
                        >
                            {SEED_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextFieldInput>
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={!name.trim() || loading}
                            sx={{ flex: '0 0 auto', borderRadius: '8px', px: 3 }}
                        >
                            Create
                        </Button>
                    </Box>
                </Box>

                {/* Seeding and deleting copy or remove every entry one at a
                    time, so they report as they go rather than freezing the
                    modal behind a spinner. */}
                {progress && (
                    <Box sx={{ p: 2, border: '1px solid #ECECEC', borderRadius: 2, mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {progress.label}
                            {' — '}
                            {progress.total
                                ? `${progress.processed ?? 0} of ${progress.total} entries`
                                : 'starting…'}
                        </Typography>
                        <LinearProgress
                            variant={progress.total ? 'determinate' : 'indeterminate'}
                            value={
                                progress.total
                                    ? ((progress.processed ?? 0) / progress.total) * 100
                                    : undefined
                            }
                        />
                    </Box>
                )}

                <Divider sx={{ mb: 1 }} />

                <List>
                    {environments.map((environment) => (
                        <ListItem key={environment.slug} sx={{ px: 0 }}>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" fontWeight={600}>
                                            {environment.name}
                                        </Typography>
                                        {environment.isDefault && (
                                            <Chip label="default" size="small" color="success" variant="outlined" />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        {`id: ${environment.slug}`}
                                        {environment.contentTypeCount != null &&
                                            ` · ${environment.contentTypeCount} model(s) · ${environment.entryCount} entry(s)`}
                                    </Typography>
                                }
                            />
                            <Tooltip title="Rename">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setEditing(environment);
                                        setEditName(environment.name);
                                    }}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title={
                                    environment.isDefault
                                        ? 'The default environment cannot be deleted'
                                        : 'Delete environment'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        sx={{ color: '#FF4842' }}
                                        disabled={environment.isDefault}
                                        onClick={() => setPendingDelete(environment)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </ListItem>
                    ))}
                </List>

                {editing && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                        <TextFieldInput
                            label={`Rename "${editing.name}"`}
                            value={editName}
                            onChange={(event) => setEditName(event.target.value)}
                            sx={{ flex: 1 }}
                        />
                        <Button variant="contained" onClick={handleRename} disabled={loading}>
                            Save
                        </Button>
                        <Button onClick={() => setEditing(null)}>Cancel</Button>
                    </Box>
                )}

                <DeleteConfirmationModal
                    deleteButtonLabel="Delete"
                    open={Boolean(pendingDelete)}
                    onClose={() => setPendingDelete(null)}
                    text={
                        <>
                            <span className="font-normal">
                                Deleting an environment permanently removes the content models and
                                entries inside it. Delete
                            </span>{' '}
                            {`"${pendingDelete?.name}"`}
                            <span className="font-normal">?</span>
                        </>
                    }
                    onConfirm={handleDelete}
                />

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {loading && <LoadingOverlay />}
            </ModalBox>
        </Modal>
    );
};

ManageEnvironmentsModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    workspaceId: PropTypes.string,
    onSuccess: PropTypes.func,
};

export default ManageEnvironmentsModal;
